import { Affix, type AffixProps } from 'antd';
import React, { useCallback, useEffect, useId, useState } from 'react';
import useDimensions from 'react-cool-dimensions';
import { down } from 'styled-breakpoints';
import { useBreakpoint } from 'styled-breakpoints/react-styled';
import { useAffixManagerStore } from './AffixManagerContext.tsx';
import { AffixOrder, computeOffsetTop } from './AffixManagerStore.ts';

export interface IManagedAffixProps extends Omit<AffixProps, 'offsetTop'> {
  /**
   * Se true, l'altezza misurata di questo componente viene registrata
   * come offset globale per tutti gli Affix con `order` superiore.
   * @default false
   */
  asGlobalOffset?: boolean;
  /**
   * Priorità di stacking. Usare le costanti `AffixOrder`.
   * @default AffixOrder.PAGE_HEADER
   */
  order?: number;
  /**
   * Offset aggiuntivo fisso sommato all'offset calcolato automaticamente.
   * Utile per aggiungere padding/spacing tra elementi.
   * @default 0
   */
  extraOffset?: number;
  /**
   * Override manuale dell'offsetTop. Se fornito, bypassa il calcolo automatico
   * dello store. Il componente continua a registrare la propria height
   * se `asGlobalOffset` è true.
   */
  offsetTop?: number;
  /**
   * Breakpoint sotto il quale l'affix viene disattivato (renderizza un div).
   * Se non fornito, l'affix è sempre attivo (anche su mobile).
   */
  breakpoint?: 'sm' | 'md' | 'lg';
}

function setRef<T>(ref: React.Ref<T> | undefined, value: T | null): void {
  if (typeof ref === 'function') {
    ref(value);
  } else if (ref && typeof ref === 'object') {
    (ref as React.MutableRefObject<T | null>).current = value;
  }
}

/**
 * Affix gestito che calcola automaticamente il proprio `offsetTop`
 * in base all'altezza cumulativa di tutti gli Affix globali precedenti.
 *
 * Utilizza `react-cool-dimensions` per misurare l'altezza del figlio
 * diretto tramite ref forwarding (senza aggiungere div wrapper al DOM).
 *
 * Per default è sempre attivo (usa `Affix` di antd). Se viene fornito
 * un `breakpoint`, sotto quella soglia renderizza un semplice `<div>`.
 *
 * Graceful degradation: senza `AffixManagerProvider` nel tree,
 * si comporta come un `Affix` con `offsetTop` da prop.
 */
export function ManagedAffix(props: IManagedAffixProps) {
  const {
    asGlobalOffset = false,
    order = AffixOrder.PAGE_HEADER,
    extraOffset = 0,
    offsetTop: offsetTopOverride,
    breakpoint,
    children,
    ...affixProps
  } = props;

  const id = useId();
  const store = useAffixManagerStore();
  // Hook chiamato sempre con un breakpoint valido (regola dei hooks).
  // Il risultato viene ignorato se nessun breakpoint è fornito.
  const matchesBreakpoint = useBreakpoint(down(breakpoint || 'sm'));
  const disableAffix = breakpoint ? matchesBreakpoint : false;

  // Misurazione altezza del figlio diretto
  const { observe, height } = useDimensions();

  // Registrazione nello store
  useEffect(() => {
    if (!store || !asGlobalOffset) return;
    store.register({ id, height: 0, order });
    return () => store.unregister(id);
  }, [store, asGlobalOffset, id, order]);

  // Aggiornamento altezza: se l'affix è disattivato (breakpoint mobile)
  // registriamo 0 perché l'elemento non è sticky e non contribuisce all'offset.
  useEffect(() => {
    if (!(store && asGlobalOffset)) return;
    store.update(id, disableAffix ? 0 : height);
  }, [store, asGlobalOffset, id, height, disableAffix]);

  // Sottoscrizione rxjs per calcolo offset reattivo
  const [computedOffset, setComputedOffset] = useState(0);
  useEffect(() => {
    if (!store) return;
    const subscription = store.snapshot$.subscribe(snapshot => {
      setComputedOffset(computeOffsetTop(snapshot.sorted, id));
    });
    return () => subscription.unsubscribe();
  }, [store, id]);

  const resolvedOffsetTop = (offsetTopOverride ?? computedOffset) + extraOffset;

  // Ref forwarding: merge observe ref con eventuale ref del child
  const mergedRefCallback = useCallback(
    (node: HTMLElement | null) => {
      observe(node);
      if (React.isValidElement(children)) {
        const childRef = (
          children as React.ReactElement & { ref?: React.Ref<HTMLElement> }
        ).ref;
        setRef(childRef, node);
      }
    },
    [observe, children],
  );

  // Clone del figlio diretto con ref mergiato
  let content: React.ReactNode = children;
  if (React.isValidElement(children)) {
    content = React.cloneElement(
      children as React.ReactElement<{ ref?: React.Ref<HTMLElement> }>,
      { ref: mergedRefCallback },
    );
  }

  // Sotto il breakpoint: renderizza un semplice div
  if (breakpoint && disableAffix) {
    return <div>{content}</div>;
  }

  return (
    <Affix offsetTop={resolvedOffsetTop} {...affixProps}>
      {content}
    </Affix>
  );
}
